import { ActiveTimer } from "@/components/nav/active-timer";
import { CommandPalette } from "@/components/nav/command-palette";
import { ConnectionStatus } from "@/components/nav/connection-status";
import { GrafanaAlertBanner } from "@/components/nav/grafana-alert-banner";
import { LiveRefresh } from "@/components/nav/live-refresh";
import { NavigationProgress } from "@/components/nav/navigation-progress";
import { Sidebar } from "@/components/nav/sidebar";
import { ProjectColorProvider } from "@/components/projects/project-color";
import { Toaster } from "@/components/ui/toaster";
import { ApiError, api } from "@/lib/api";
import { deriveTitleFromPath, setDocumentTitle } from "@/lib/document-title";
import type { GlobalRole } from "@prisma/client";
import {
  Outlet,
  createFileRoute,
  redirect,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Pathless layout route for the signed-in (app) shell. Every page
 * that needs the sidebar + auth gate lives under this layout — by
 * convention, anything under `client/src/routes/_app/`.
 *
 * Mirrors the Next-side `src/app/(app)/layout.tsx`. The loader
 * fetches the user and the project-manager flag in
 * one round-trip (`/api/me/layout`) so the sidebar renders with
 * full data and the rest of the route tree doesn't waterfall.
 *
 * Auth redirect: when `/api/me/layout` returns 401 the loader
 * throws an `ApiError`; the `errorComponent` renders a sign-in
 * prompt with a plain `<a href="/login">` so the browser does a
 * full navigation back to the Next side (login is owned by Next
 * during phase 4 option A). We deliberately do NOT auto-redirect
 * — the dev flow has Vite, not Next, serving the SPA shell, so a
 * `window.location` hop straight to /login bounces through the
 * Next dev server and back, which is jarring in development.
 */

type LayoutData = {
  user: {
    id: string;
    email: string;
    name: string | null;
    globalRole: GlobalRole | null;
    gitlabUserId: string | null;
  };
  isProjectManager: boolean;
  /** Set when an admin started a read-only "view as user" overlay
   *  — `user` above is the target user; this carries the real
   *  admin identity so the banner can name them. */
  viewAs: {
    realUserId: string;
    realUserName: string | null;
    realUserEmail: string;
  } | null;
  /** Customer account (guest-only on all project memberships) —
   *  sidebar collapses to the project list, project + WI detail
   *  pages hide team / time / assignee panels, and the only
   *  permitted mutations are commenting + waiting-on-client. */
  isCustomer: boolean;
  /** Populated only for customer accounts — the projects they're
   *  a guest in, surfaced directly in the sidebar instead of a
   *  generic "Projects" link. Empty array for everyone else. */
  customerProjects: Array<{ id: string; name: string; slug: string }>;
  /** Admin-only — the registered agents, listed under the sidebar's
   *  collapsible "Agents" entry. Empty for everyone else. */
  agents: Array<{ slug: string; name: string }>;
  /** Per-project colour overrides, keyed by project id — only the projects
   *  that have one. Everything else derives its colour from its id, so this
   *  is usually empty and no other payload has to carry a colour at all. */
  projectColors: Record<string, string>;
};

/**
 * The last layout payload that loaded successfully.
 *
 * The shell's data — who you are, your role, your projects — doesn't change
 * from one minute to the next, but the loader that fetches it re-runs on every
 * revalidation: each live-socket event, each reconnect, each navigation. Until
 * this cache existed, one failed revalidation threw, and the route's
 * `errorComponent` replaced the entire working application with a "Connection
 * lost" page — losing whatever the user had on screen because a background
 * refresh of data we already had happened to hit a blip.
 *
 * So a revalidation that fails is answered from here instead. The page stays
 * exactly as it is and the connection toast does the talking (see
 * `ConnectionStatus`). The full-page error is then reachable only where it's
 * the honest answer: a cold start with nothing to show yet.
 */
let lastGoodLayout: LayoutData | null = null;

export const Route = createFileRoute("/_app")({
  loader: async () => {
    try {
      const data = await api.get<LayoutData>("/api/me/layout");
      lastGoodLayout = data;
      return data;
    } catch (err) {
      // No / expired session — bounce to the sign-in page. Drop the cache
      // first: whoever signs in next must not inherit this identity.
      if (err instanceof ApiError && err.status === 401) {
        lastGoodLayout = null;
        throw redirect({ to: "/login" });
      }
      // Serve what we had. Deliberately not limited to network errors: a 500
      // from this endpoint while the user is mid-task is no more of a reason
      // to throw their work away than a dropped packet is.
      if (lastGoodLayout) return lastGoodLayout;
      throw err;
    }
  },
  component: AppLayout,
  errorComponent: AppLayoutError,
});

/**
 * Sets the tab title from the active route's pathname on every
 * navigation. Detail routes (work items, customers, …) can override
 * with `useDocumentTitle` once they have richer data on the screen
 * — that hook fires after this default lands, so the user sees a
 * sensible page name immediately and the richer one when the loader
 * resolves.
 */
function RouteTitle() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    setDocumentTitle(deriveTitleFromPath(pathname));
  }, [pathname]);
  return null;
}

function AppLayout() {
  const { user, isProjectManager, viewAs, isCustomer, customerProjects, agents, projectColors } =
    Route.useLoaderData();
  // Off-canvas drawer state — only meaningful below the `lg`
  // breakpoint. At `lg`+ the sidebar is permanent and this state is
  // ignored (the drawer wrapper drops its mobile-only classes).
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [exiting, setExiting] = useState(false);

  async function exitViewAs() {
    if (exiting) return;
    setExiting(true);
    try {
      await api.delete("/api/admin/view-as");
    } finally {
      // Hard reload so every route loader refetches under the real
      // identity (router cache might otherwise hold the target's
      // data on routes that don't auto-revalidate).
      globalThis.location.assign("/");
    }
  }

  return (
    <ProjectColorProvider overrides={projectColors ?? {}}>
      <div className="flex h-screen overflow-hidden">
        {/* Dimmed backdrop — mobile only, only while the drawer is open.
          Tapping it closes the drawer. */}
        {drawerOpen && (
          <button
            type="button"
            aria-label="Close menu"
            data-testid="sidebar-backdrop"
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          />
        )}

        {/* Sidebar: permanent at `lg`+, an off-canvas drawer below it.
          The drawer slides in/out with a `translate-x` transition. */}
        <div
          className={`fixed inset-y-0 left-0 z-40 transform transition-transform duration-200 lg:static lg:z-auto lg:transform-none ${
            drawerOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
        >
          <Sidebar
            globalRole={user.globalRole ?? null}
            isProjectManager={isProjectManager}
            userName={user.name ?? user.email ?? ""}
            isCustomer={isCustomer}
            customerProjects={customerProjects}
            agents={agents ?? []}
            onNavigate={() => setDrawerOpen(false)}
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Slim mobile top bar — hidden at `lg`+ where the permanent
            sidebar already carries the logo. */}
          <header className="flex h-12 shrink-0 items-center gap-2 border-b bg-sidebar px-3 lg:hidden">
            <button
              type="button"
              aria-label="Open menu"
              data-testid="sidebar-toggle"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
            >
              <Menu className="h-5 w-5" />
            </button>
            <img src="/netlion-mark.png" alt="Netlion" className="h-6 w-6 object-contain" />
            <span className="font-heading text-base font-semibold tracking-tight text-primary">
              HUB
            </span>
          </header>

          {viewAs && (
            <div
              data-testid="view-as-banner"
              className="flex items-center justify-between gap-3 border-b border-amber-300/60 bg-amber-100 px-4 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200"
            >
              <div className="min-w-0 truncate">
                <span className="font-medium">Viewing as {user.name ?? user.email}</span>
                <span className="ml-2 text-amber-800/80 dark:text-amber-300/80">
                  — read-only. You're {viewAs.realUserName ?? viewAs.realUserEmail}.
                </span>
              </div>
              <button
                type="button"
                onClick={exitViewAs}
                disabled={exiting}
                className="shrink-0 rounded-md border border-amber-400 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-200 disabled:opacity-60 dark:border-amber-500/50 dark:bg-amber-500/20 dark:text-amber-100"
              >
                {exiting ? "Exiting…" : "Exit view-as"}
              </button>
            </div>
          )}

          <GrafanaAlertBanner enabled={!isCustomer} />

          <main className="min-w-0 flex-1 overflow-y-auto">
            {/* `pb-28` reserves clearance for the fixed ActiveTimer cluster
              in the bottom-right corner — without it the timer button
              (and its expanding quick-create pills on hover) would sit
              on top of bottom-of-page content like pagination. Sized to
              the worst case: timer pill (~44 px) + bottom-4 margin
              (16 px) + comfort buffer. Customers don't get the timer
              cluster so they don't need the padding either. */}
            <div
              className={
                isCustomer
                  ? "px-4 pt-5 pb-6 lg:pl-6 lg:pr-6 lg:pt-6 2xl:pl-10"
                  : "px-4 pt-5 pb-28 lg:pl-6 lg:pr-[76px] lg:pt-6 lg:pb-28 2xl:pl-10"
              }
            >
              <Outlet />
            </div>
          </main>
        </div>

        <RouteTitle />
        <CommandPalette />
        {/* Customers don't track time, so the floating timer +
          quick-create cluster is hidden for them. */}
        {!isCustomer && <ActiveTimer />}
        <NavigationProgress />
        <LiveRefresh />
        <ConnectionStatus />
        <Toaster />
      </div>
    </ProjectColorProvider>
  );
}

/**
 * Full-screen fallback for when the layout loader fails **with nothing to
 * show** — a cold start against a server that isn't answering. Once the shell
 * has loaded even once, a later failure is served from `lastGoodLayout` and
 * never reaches here; the page stays put and the connection toast reports it.
 *
 * Because it now only appears when the user is staring at an empty app
 * anyway, it retries by itself rather than waiting to be clicked. The button
 * stays for anyone who doesn't want to wait.
 */
function AppLayoutError({ error }: { error: Error }) {
  const router = useRouter();
  const is401 = error instanceof ApiError && error.status === 401;
  // A `TypeError` from `fetch` (no HTTP response) is a transient
  // network failure — `ERR_NETWORK_CHANGED`, dropped WiFi, VPN
  // reconnect. The loader already retries idempotent GETs (see
  // `api.ts`); if it still lands here the blip outlasted the retries,
  // so offer a one-click recovery instead of forcing a full reload.
  //
  // A bug in a component throws `TypeError` too ("Cannot read properties
  // of undefined"), and calling that a lost connection sends whoever
  // debugs it after the network for an hour. Only fetch's own wording
  // counts as a network failure; anything else shows its real message.
  const isNetwork =
    !is401 &&
    error instanceof TypeError &&
    /failed to fetch|networkerror|load failed|network request failed/i.test(error.message);
  const [retrying, setRetrying] = useState(false);

  const retry = async () => {
    setRetrying(true);
    try {
      // Re-run the layout loader in place — no full page reload, so
      // the user keeps their route once the network is back.
      await router.invalidate();
    } finally {
      setRetrying(false);
    }
  };

  // Keep trying on our own. A server that comes back a few seconds later
  // should find the app already recovered, not a screen waiting for a click.
  // Not for a 401 — no amount of retrying signs anyone back in.
  useEffect(() => {
    if (is401) return;
    const id = setInterval(() => void router.invalidate().catch(() => {}), 5_000);
    return () => clearInterval(id);
  }, [is401, router]);

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="rounded-lg border bg-card p-8 text-center shadow-sm max-w-md">
        <h1 className="font-heading text-xl font-bold">
          {is401 ? "You are signed out" : isNetwork ? "Connection lost" : "Could not load layout"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {is401 ? (
            <>
              Your session expired.{" "}
              <a href="/login" className="underline">
                Sign in
              </a>{" "}
              to continue.
            </>
          ) : isNetwork ? (
            "Your network connection dropped. Check your connection and try again."
          ) : (
            error.message
          )}
        </p>
        {!is401 && (
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {retrying ? "Retrying…" : "Retry"}
          </button>
        )}
      </div>
    </div>
  );
}
